import { Component, OnInit, ViewChild } from '@angular/core';
import * as firebaseui from 'firebaseui'
import { Validators, FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { FirebaseService } from '../../services/firebase.service'
import { Logger } from 'src/app/services/datadog.logger.service';
import { FirebaseResponseObject } from '../../models/FirebaseResponseObject'
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import * as moment from 'moment'

enum ViewStates {
  PersonalInformation,
  PreviousFilledOutForm,
  FillOutForm
}

@Component({
  selector: 'app-customer',
  templateUrl: './customer.component.html',
  styleUrls: ['./customer.component.less', '../../../assets/style.less']
})
/* This class is responsible for displaying the customer dashboard and all the forms that the customer
needs to fill out */
export class CustomerComponent implements OnInit {

  // Responsible and necessary for setting up the Reactive Form
  customerFormGroup: FormGroup;

  // All the previous filled out forms for this customer
  filledOutForms: FirebaseResponseObject[];

  // A list of Form Template objects.  This is the form templates for all the services for an appointment
  formTemplates: any[];

  // The service that the user has just clicked
  selectedService;

  // The form templates that show when a user selects a service
  formTemplatesForService = {};

  // The Id of the artist this page is for
  artistId: string;

  // When set to true than the page is in the saving state
  saving: boolean

  // Stores any server error
  error;

  // Current logged in user
  user: firebase.User;

  viewState = 'dashboard';

  // Whether or not the user has tried to submit this form
  submitted: boolean = false;

  canFillOutForms: boolean = false;

  companyName: string

  // Date of the appointment that is being created.
  appointmentDate: Date

  // An array of appointments that the user has already created
  appointments: any[]

  // Any appointments that were done in the past
  pastAppointments: any[]

  // Appointments coming up in the future
  upcomingAppointments: any[]

  // An array of Service objects
  services: any[]

  // The appointment that is currently selected
  selectedAppointment: FirebaseResponseObject;

  // Any error that happens when deleting an appointment
  deleteAppointmentError: string

  // Message to display on the bottom toast when a successful action is taken
  toastSuccess: string

  // Message to display on the bottom toast when an error occurs
  toastError: string

  hideNav: boolean = false

  //Holder for selectedAppointment.data.services
  /**
   * I created this because if you keep calling getAllFormsForServices(selectedAppointment.data.services), 
   * it will create an infinite loop. See https://stackoverflow.com/a/74374331/19354849
   */
  allFormsForService: any[]

  // Whether this user is new to using this app
  gotoNewAppointmentAfterPersonalInformation = true

  referallTypes = ['Friend', 'Co-worker', 'Yelp', 'Google', 'Google Ad', 'Facebook', 'Facebook Ad', 'Instagram', 'Instagram ad', 'Other social media',
    'salon', 'Another beauty professional', 'Other']

  PERSONAL_INFORMATION = "personal_information"

  getSelectedAppointment() {

  }

  /**
   * 
   * @param firebaseService Responsible for making all calls to Firebase
   * @param route
   * @param logger - Implement instance of datadog logger
   * @param activatedRoute
   */
  constructor(
    private firebaseService: FirebaseService,
    private route: Router,
    private logger: Logger,
    private activatedRoute: ActivatedRoute) {

    this.logger.info('Entering customer component class. ')

    this.firebaseService.setAuthToken()

    this.customerFormGroup = new FormGroup(this.createFormValidationObject())
    this.artistId = this.activatedRoute.snapshot.paramMap.get("artistId")
    this.viewState = this.activatedRoute.snapshot.paramMap.get("viewstate")

    const selectedAppointmentId = this.activatedRoute.snapshot.paramMap.get("appointmentId")
    if (selectedAppointmentId) {
      this.firebaseService.getAppointmentWithId(selectedAppointmentId).subscribe(selectedAppointment => {
        this.logger.info(`Selected appointment: ${selectedAppointment}`)
        this.selectedAppointment = selectedAppointment
        this.allFormsForService = this.getAllFormsForServices(this.selectedAppointment.data.services)
        console.log('allFormsForService: ', this.allFormsForService)
    
      })
    }

        this.firebaseService.getArtist(this.artistId).subscribe(artist => {
      this.logger.info('Successfully retrieved artist from firestore')
      // this.firebaseService.log("Successfully retrieved artist from firestore")
      this.companyName = artist.get("businessName")
      this.firebaseService.storeArtistInformation(this.companyName, this.artistId)
    }, err => {
      this.logger.error("Error retrieving artist from firestore", err ? err : '')
      // this.firebaseService.log("Error retrieving artist from firestore", err)
    })

    this.firebaseService.isLoggedIn()
      .then(user => {
        this.user = user
        this.firebaseService.storeLoggedInUserInformation(user.uid, user.email)
        this.getAllAppointments(user.uid)
        this.getFilledOutForms(user.uid)
        this.getClientInfo(user.uid)
      })

    this.firebaseService.getServicesForArtistWithId(this.artistId).subscribe(services => {
      this.services = services
      this.firebaseService.getAllFormsForServicesFromFirebase(this.services.map(service => service.id), this.artistId).then(formTemplates => {
        this.logger.info('Successfully retrieved all forms for services from Firestore', { formTemplates })
        // this.firebaseService.log("Successfully retrieved all forms for services from Firestore")
        this.formTemplates = formTemplates
      }).catch(err => {
        this.logger.error('Error retrieving all forms for services from Firestore', err ? err : '')
        // this.firebaseService.log("Error retrieving all forms for services from Firestore", err)
      })
    })



    if (!this.viewState) {
      this.viewState = 'dashboard'
    }

  }

  /**
   * Get all the forms that are for a specific service.  Grabs the services from the local cache of services
   * @param serviceId The id of the service to get the forms for
   */
  getFormsForServiceWithId(serviceId: number) {
    this.logger.info(`Entering method getFormsForServiceWithId.
    Parameters: serviceId=${serviceId}`, { serviceId })
    return this.formTemplates.filter((element, index, array) => {
      this.logger.info('Leaving method getFormsForServiceWithId')
      return element.services.indexOf(serviceId) != -1
    })
  }

  showFormWithId(templateId: number, services: number[], appointmentId: string) {
    this.logger.info(`Entering method showFormWithId.
    Parameters: templateId=${templateId}, services=${services}, appointmentId=${appointmentId}`, { templateId, services, appointmentId })
    if (this.firebaseService.isLoggedIn()) {
      this.logger.info('Leaving method showFormWithId')
      this.route.navigateByUrl(`forms/services/${services}/artist/${this.artistId}/appointment/${appointmentId}/${templateId}`)
    }
  }

  /**
   * Get all the forms that need to be filled out for the list of services provided
   * @param serviceIds The services that the user will be getting done for this appointment
   */
  getAllFormsForServices(serviceIds: number[]) {
    //Log below has an insanely high logging rate (10k+ per minute). I commented it out for now. 

    // this.logger.info(`Entering method getAllFormsForServices.
    // Parameters: serviceIds=${serviceIds}`, { serviceIds })
    let appointmentFormTemplates = []

    if (!this.formTemplates) { return }

    serviceIds.map(id => {
      this.formTemplates.map(template => {
        if (template.services.indexOf(id) == -1) {
          return
        }

        if (appointmentFormTemplates.indexOf(template) == -1) {
          appointmentFormTemplates.push(template)
        }
      })
    })

    // this.logger.info('Exiting method getAllFormsForServices')
    return appointmentFormTemplates
  }

  /**
   * Gets all the service objects for the services that are in the appointment
   * An appointment contains a list of service ids.  This method gets the service objects for those ids. This allows us to display the service names
   */
  getAllServicesForAppointment(serviceIds: number[]) {
    //Log below has an insanely high logging rate (10k+ per minute). I commented it out for now. 
    // this.logger.info(`Entering method getAllServicesForAppointment.
    // Parameters: serviceIds=${serviceIds}`, { serviceIds })

    if (!this.services) { 
      this.logger.info('Exiting method getAllServicesForAppointment')
      return 
    }
    const services = this.services.filter((element, index, array) => {
      return serviceIds.indexOf(element.id) != -1
    })

    // this.logger.info('Exiting method getAllServicesForAppointment')
    return services
  }

  convertServicesToArrayOfServiceNames(services: any[]) {
    //Log below has an insanely high logging rate (10k+ per minute). I commented it out for now. 

    // this.logger.info(`Entering method convertServicesToArrayOfServiceNames.
    // Parameters: services=${services}`)
    if (!services) { 
      this.logger.error('No services received. Exiting method convertServicesToArrayOfServiceNames')
      return 
    }
    // this.logger.info('Exiting method convertServicesToArrayOfServiceNames')
    return services.reduce((acc, val) => acc.concat(val.service), []).join(', ')
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe(params => {
      this.viewState = params['viewstate']
      if (!this.viewState) {
        this.viewState = 'dashboard'
      }
    })


  }

  /**
   * Every filled out form has an appointmentId and a formId and that's how we can tell if a user has filled out a form
   * If there is no form filled out with the given formId and appointmentId than that means that that specific form
   * has not been filled out yet for the specified appointment
   * 
   * @param appointmentId The id of the appointment to check whether the form is complete for
   * @param formId The id of the form to check if completed
   */
  isFormComplete(appointmentId: string, formId: string) {
    //Log below has an insanely high logging rate (10k+ per minute). I commented it out for now. 

    // this.logger.info(`Entering method isFormComplete.
    // Parameters: appointmentId=${appointmentId}, formId=${formId}`, { appointmentId, formId })

    if (this.filledOutForms.filter(form => {
      return form.data.form_template_id == formId && form.data.appointment_id == appointmentId
    }).length == 0) {
      // this.logger.info('Exiting method isFormComplete')
      return false
    }
    // this.logger.info('Exiting method isFormComplete')
    return true
  }

  /**
   * Get all the appointments for a client
   * @param userId The user id to get all the appointments for
   */
  async getAllAppointments(userId: string) {
    //Log below has an insanely high logging rate (10k+ per minute). I commented it out for now. 

    // this.logger.info(`Entering method getAllAppointments.
    // Parameters: userId=${userId}`, { userId })

    this.firebaseService.getAppointmentsForClient(this.user.uid).subscribe(appointments => {
      this.logger.info('Retrieved the appointments for the client')
      // this.firebaseService.log("Retrieved the appointments for the client")
      this.appointments = appointments
      this.pastAppointments = appointments.filter((element) => {
        // this.logger.info('Exiting method getAllAppointments')
        return moment(element.data.date).toDate() < new Date() && element.data.deleted != true
      })

      this.upcomingAppointments = appointments.filter((element) => {
        // this.logger.info('Exiting method getAllAppointments')
        return moment(element.data.date).toDate() >= new Date() && element.data.deleted != true
      })
    }, err => {
      this.logger.error('Error retrieving the appointments for the client', err ? err : '')
      // this.firebaseService.log("Error retrieving the appointments for the client", err)
    })
  }

  /**
   * When the user presses the fill out forms for your next appointment button take them to the schedule appointment screen
   */
  fillOutFormsForAppointmentPressed() {
    this.route.navigateByUrl(`${this.artistId}/appointment`)
  }

  getClientInfo(customerId: string) {
    this.logger.info(`Entering method getClientInfo.
    Parameters: customerId=${customerId}`, { customerId })

    this.firebaseService.getCustomer(customerId)
      .subscribe(customer => {

        //check if this user has any appointments, if this user doesn't then we will treat them as a new user
        if(!this.appointments || this.appointments.length == 0) {
          this.gotoNewAppointmentAfterPersonalInformation = true
        } else {
          /* If there are already appointments, then by definition this user 
             shouldn't need to go straight to an appointment after filling out 
             personal information
          */
          this.gotoNewAppointmentAfterPersonalInformation = false
          this.hideNav = false
        }

        if (!customer.info) {
          this.forceFillOutForms()
          return
        }

        this.customerFormGroup.setValue({
          date_of_birth: customer.info.date_of_birth != null ? customer.info.date_of_birth : "",
          occupation: customer.info.occupation != null ? customer.info.occupation : "",
          home_address: customer.info.home_address != null ? customer.info.home_address : "",
          client_name: customer.info.client_name != null ? customer.info.client_name : "",
          city: customer.info.city != null ? customer.info.city : "",
          state: customer.info.state != null ? customer.info.state : "",
          zip_code: customer.info.zip_code != null ? customer.info.zip_code : "",
          home_phone: customer.info.home_phone != null ? customer.info.home_phone : "",
          cell_phone: customer.info.cell_phone != null ? customer.info.cell_phone : "",
          referred: customer.info.referred != null ? customer.info.referred : "",
          emergency_contact_name: customer.info.emergency_contact_name != null ? customer.info.emergency_contact_name : "",
          emergency_contact_phone: customer.info.emergency_contact_phone != null ? customer.info.emergency_contact_phone : "",
          age: customer.info.age != null ? customer.info.age : ""
        })

        if (this.customerFormGroup.invalid) {
          //always show the form to fill out personal information if we're missing data (e.g. when validation returns invalid)
          this.forceFillOutForms()
        } else {
          this.canFillOutForms = true
        }
      })
  }

/**
 * If the values from the server for the customer are missing anything than we need to make sure that
 * we don't allow the user to fill out a form without first submitting this form
 */
  forceFillOutForms () {
    this.viewState = this.PERSONAL_INFORMATION
    this.hideNav = true

    // If the values from the server for the customer are missing anything than we need to make sure that we don't allow the user to
    // fill out a form without first submitting this form
    this.canFillOutForms = false
  }

  navigateTo(state: string) {
    this.logger.info(`Entering method navigateTo.
    Parameters: state=${state}`, { state })

    this.route.navigateByUrl(`${this.artistId}/${state}`)
    this.logger.info('Exiting method navigateTo')

  }

  /**
   * Create the object used for the customer form validation for the customer form group
   */
  createFormValidationObject() {

    return {
      date_of_birth: new FormControl('', [Validators.required]),
      occupation: new FormControl('', [Validators.required]),
      home_address: new FormControl('', [Validators.required]),
      client_name: new FormControl('', [Validators.required]),
      city: new FormControl('', [Validators.required]),
      state: new FormControl('', [Validators.required]),
      zip_code: new FormControl('', [Validators.required]),
      home_phone: new FormControl('', [Validators.required]),
      cell_phone: new FormControl('', [Validators.required]),
      referred: new FormControl('', [Validators.required]),
      emergency_contact_name: new FormControl('', [Validators.required]),
      emergency_contact_phone: new FormControl('', [Validators.required]),
      age: new FormControl('', [Validators.required])
    }
  }

  getControl(controlName) {
    // this.logger.info(`Entering method getControl.
    // Parameters: controlName=${controlName}`, { controlName })
    // this.logger.info('Exiting method getControl')
    return this.customerFormGroup.get(controlName)
  }


  /**
   * If the customer is logged in, then update the customer's information with the information they entered on the client info form
   */
  onSubmit() {

    this.saving = true

    // Set this value to true so that we can now notify the user if they have incorrect form data
    this.submitted = true
    // If the customer is logged in, than update the customer
    this.firebaseService.isLoggedIn()
      .then(user => {
        if (user) {
          if (this.customerFormGroup.invalid) {
            this.error = "Sorry, there's an error on the form. Please make sure you fill out all fields."
            this.saving = false
            return
          }

          this.error = undefined

          let customerInfo = this.customerFormGroup.value
          customerInfo.date_updated = new Date()

          this.customerFormGroup.value.date_updated = new Date()
          this.firebaseService.updateCustomerInfo(user.uid, customerInfo, user)
            .then(result => {
              this.logger.info('Updated user/customer information successfully')
              // this.firebaseService.log("Updated user/customer information successfully")
              this.saving = false
              this.error = null
              this.hideNav = false
              this.route.navigateByUrl(`${this.artistId}/successful_submission`)
            })
            .catch(error => {
              this.error = error
              this.saving = false
              this.logger.error('Error updating user/customer information', error ? error : '')
              // this.firebaseService.log("Error updating user/customer information", error)
            })
        } else {
          // Log the user out
        }
      })
  }

  getFormattedDate(date: string) {
    //Log below has an insanely high logging rate (10k+ per minute). I commented it out for now. 
    // this.logger.info(`Entering method getFormattedDate.
    // Parameters: date=${date}`)

    // this.logger.info('Exiting method getFormattedDate')
    return moment(date).format('dddd, MMMM Do YYYY')
  }

/**
 * It deletes an appointment from the upcoming and past appointments arrays, and then updates the
 * appointment in the database to have a deleted property of true
 * @param {string} appointmentId - The id of the appointment to be deleted
 */
  deleteAppointmentById(appointmentId: string) {
    this.logger.info(`Entering method deleteAppointmentById.
    Parameters: appointmentId=${appointmentId}`, { appointmentId })

    this.deleteAppointmentError = undefined
    this.pastAppointments = this.pastAppointments.filter(element => element.data.id != appointmentId)
    this.upcomingAppointments = this.upcomingAppointments.filter(element => element.data.id != appointmentId)
    this.firebaseService.updateAppointment(appointmentId, { deleted: true }).catch(error => {
      this.toastError = "Error deleting appointment"
      this.logger.error('Error deleting appointment', error)
      this.logger.info('Exiting method deleteAppointmentById')

      setTimeout(() => this.toastError = undefined, 3000)
    }).then(() => {
      this.toastSuccess = "Appointment Deleted"
      this.logger.info(`Succesfully deleted appointment: ${appointmentId}`)
      setTimeout(() => this.toastSuccess = undefined, 3000)
      this.logger.info('Exiting method deleteAppointmentById')
    })
  }

  //

  /**
   * Goto a filled out form so that the user can edit it
   * @param filledFormId The id of the filled form to show
   * @param templateId The id of the form template to show
   */
  gotoFilledOutForm(filledFormId: string, templateId: string) {
    this.logger.info(`Entering method gotoFilledOutForm.
    Parameters: filledFormId=${filledFormId}, templateId=${templateId}`, { filledFormId, templateId })

    this.route.navigateByUrl(`/forms/${templateId}/artist/${this.artistId}/edit/${filledFormId}`)
    this.logger.info('Exiting method gotoFilledOutForm')

  }

  /**
   * Gets all the forms that the current customer has filled out
   */
  getFilledOutForms(userId: string) {
    this.logger.info(`Entering method getFilledOutForms.
    Parameters: userId=${userId}`, { userId })


    this.firebaseService.getFilledFormsForCustomer(userId)
      .subscribe(forms => {
        this.logger.info(`Retrieved all filled forms for customer with id: ${userId}`)
        // this.firebaseService.log(`Retrieved all filled forms for customer with id: ${userId}`)
        this.filledOutForms = forms
        this.logger.info('Exiting method getFilledOutForms')
      }, err => {
        this.logger.error(`Error retrieving all filled forms for user with id: ${userId}`, err ? err : '')
        this.logger.info('Exiting method getFilledOutForms')
        // this.firebaseService.log(`Error retrieving all filled forms for user with id: ${userId}`, err)
      })

  }

  /**
   * Navigate to an empty form template to allow the user to fill out the form
   * @param templateId The id of the template to show
   */
  navigateToNewFormTemplate(templateId: string) {
    this.logger.info(`Entering method navigateToNewFormTemplate.
    Parameters: templateId=${templateId}`, { templateId })

    this.route.navigateByUrl(`forms/${templateId}/artist/${this.artistId}`)
    this.logger.info('Exiting method navigateToNewFormTemplate')

  }

  /**
   * Logout the user and send them to the login page
   */
  logout() {
    this.firebaseService.logout()
    this.route.navigate(['login'], { queryParams: { returnUrl: this.route.url } })
  }

}
